import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { config } from '@iheartjane/config';
import { debugLog } from '@iheartjane/tracking';
import { Storage } from '@iheartjane/utils';

import { getLocalStorageWithTTL } from '@jane/shared/util';

import { api } from '../api/api';

export const analyticsUrl = () => `${config.idAnalyticsUrl}`;

export interface SendAnalyticsData {
  fallbackId?: string;
  fpId?: string;
  jdid: string;
  juid?: string | number;
  source: string;
}

export function sendAnalytics(data: SendAnalyticsData) {
  if (!config.idAnalyticsUrl) return;

  // Validate required data
  if (!data?.source) {
    return;
  }

  // Check if we've already sent analytics for this source in the last 15 minutes
  const dedupeKey = `analytics_sent_${data.source}`;
  if (getLocalStorageWithTTL(dedupeKey)) {
    // Already sent analytics for this source within the TTL period
    return;
  }

  api.post(analyticsUrl(), data).catch(() => {
    return;
  });

  // Record that we sent analytics for this source with a 15-minute TTL
  setLocalStorageWithTTLInMinutes(dedupeKey, 'sent', 15);
}

function setLocalStorageWithTTLInDays(
  key: string,
  value: string,
  ttlInDays: number
) {
  if (!key || !value) {
    debugLog('Trying to set an empty value!');
    return;
  }
  const expiry = Date.now() + ttlInDays * 24 * 60 * 60 * 1000;
  Storage.set(key, JSON.stringify({ value, expiry }));
}

function setLocalStorageWithTTLInMinutes(
  key: string,
  value: string,
  ttlInMinutes: number
) {
  if (!key || !value) {
    debugLog('Trying to set an empty value!');
    return;
  }
  const expiry = Date.now() + ttlInMinutes * 60 * 1000;
  Storage.set(key, JSON.stringify({ value, expiry }));
}

export const getFingerprintId = async (linkedId?: string | undefined) => {
  const legacyFpKey = 'j_eyeD';
  const newFpKey = 'cachedJdid';
  const timeout = config.fingerprintTimeoutMs ?? 10000;
  try {
    const fp = await FingerprintJS.load({
      apiKey: config.fingerprintApiKey,
      endpoint: [config.fingerprintEndpoint, FingerprintJS.defaultEndpoint],
      scriptUrlPattern: [
        `${config.fingerprintScriptPath}?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`,
        FingerprintJS.defaultScriptUrlPattern,
      ],
      storageKey: 'eyeD',
    });
    const result = await fp.get({ linkedId, timeout });
    if (result?.visitorId) {
      setLocalStorageWithTTLInDays(legacyFpKey, result.visitorId, 1);
      setLocalStorageWithTTLInDays(newFpKey, result.visitorId, 1);
      return result.visitorId;
    }
    return null;
  } catch (error) {
    console.debug('FP Fetch Error: ', error);
    return null;
  }
};
